import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { Cart } from 'shared/types/cart';
import { DiscountCode } from 'shared/types/cart/Discount';
import { Order } from 'shared/types/cart/Order';
import { Variant } from 'shared/types/product';
import useSWR, { mutate } from 'swr';
import useSWRImmutable from 'swr/immutable';
import { getParentLineItems } from 'components/commercetools-ui/organisms/cart/helpers';
import useI18n from 'helpers/hooks/useI18n';
import mapCosts from 'helpers/utils/mapCosts';
import { sdk } from 'sdk';
import {
  PersonalizedCartPayload,
  SetCartShippingPayload,
  UpdateCartLineItemPayload,
} from 'sdk/composable-commerce/types/payloads/CartPayloads';
import { revalidateOptions, useAccount } from 'frontastic';
import { invalidateCartAndMiniCarts, miniCartsKey } from './helper';
import { CartDetails, UseCartReturn } from './types';

const DEDUPING_INTERVAL = 3000; // 3 secs

const PLATFORM_TAX_MODE = 'Platform'; // Tax mode used for platform-based tax calculations

export interface OrderHistoryFilters {
  limit?: number;
  cursor?: string;
  dateFrom?: string;
  dateTo?: string;
  orderState?: string | string[];
  // deliveryMethod?: string;
  date?: string;
  sortAttributes?: Record<string, 'asc' | 'desc'>;
  fulfillmentType?: string;
}
const LARGE_CART_LINE_ITEM_LIMIT = 700;
const useCart = (cartIdProp?: string): UseCartReturn => {
  const { slug } = useParams();
  const cartId = (slug?.[0] === 'cart' ? slug?.[1] : '') || cartIdProp || '';
  const payloadForCart = {
    query: { cartId: cartId },
  };
  const extensions = sdk.composableCommerce;

  const { currency } = useI18n();

  const getCartKey = useCallback((cartId?: string) => (cartId ? ['/action/cart/getCart', cartId] : null), []);
  const { loggedIn } = useAccount();
  const result = useSWR(loggedIn ? getCartKey(cartId) : null, () => extensions.cart.getCart(payloadForCart), {
    ...revalidateOptions,
    revalidateIfStale: true,
    dedupingInterval: DEDUPING_INTERVAL,
  });

  const { data: shippingMethodsResponse } = useSWRImmutable('/action/cart/getShippingMethods/pdp', () =>
    extensions.cart.getShippingMethods(),
  );

  const shippingMethodsData = shippingMethodsResponse?.isError
    ? { data: [] }
    : { data: shippingMethodsResponse?.data ?? [] };

  const miniCartsResult = useSWR(loggedIn ? miniCartsKey : null, () => extensions.cart.getMiniCarts(), {
    ...revalidateOptions,
    dedupingInterval: DEDUPING_INTERVAL,
  });

  const miniCartsLoading = !miniCartsResult.data && !miniCartsResult.error;

  const [cartData, setCartData] = useState<Cart>();

  useEffect(() => {
    if (result.data?.isError || result.isLoading || result.isValidating) {
      setCartData(undefined);
    } else {
      setCartData(result.data?.data);
    }
  }, [result]);

  const isLoading = result.isLoading || result.isValidating;

  const shippingMethods = shippingMethodsData;
  const miniCarts = miniCartsResult.data?.isError ? [] : (miniCartsResult.data?.data ?? []);

  const validLineItems = getParentLineItems((cartData as Cart)?.lineItems || []);
  const totalItems = validLineItems?.reduce((acc, curr) => acc + (curr.count as number), 0) ?? 0;

  const isEmpty = !cartData?.lineItems?.length;

  const isShippingAccurate = !!cartData?.shippingInfo;

  const hasOutOfStockItems = !!cartData?.lineItems?.some((lineItem) => !lineItem.variant?.isOnStock);

  const transaction = useMemo(() => mapCosts({ cart: cartData, currency }), [cartData, currency]);

  const addItem = useCallback(
    async (variant: Variant, quantity: number) => {
      const extensions = sdk.composableCommerce;

      const payload = {
        variant: {
          sku: variant.sku,
          count: quantity,
        },
      };

      const res = await extensions.cart.addItem(payload);
      void invalidateCartAndMiniCarts(getCartKey, cartId, res);
    },
    [cartId],
  );

  const getCartById = useCallback(async (cartId: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      query: { cartId },
    };

    const res = await extensions.cart.getCart(payload);
    if (!res.isError) {
      return res?.data ?? {};
    }
  }, []);

  // Update a cached cart (and that cart as part of the cached mini carts list) with new data.
  // We send revalidate=false to avoid revalidation. Used for optimistic caching, before we call
  // the backend with new data. Call this again to update it with the response from the backend
  // to make sure it is up to date.
  const updateCachedCart = useCallback(
    (targetCartId: string, res?: any) => {
      if (!targetCartId) return;

      if (typeof res !== 'undefined') {
        mutate(getCartKey(targetCartId), res, false);

        const updatedCart = (res?.data ?? res) as Cart | undefined;
        if (updatedCart?.cartId) {
          mutate(
            miniCartsKey,
            (current: any) => {
              if (current?.isError) return current;

              const currentMiniCarts = Array.isArray(current?.data) ? current.data : [];
              const nextMiniCarts = currentMiniCarts.map((miniCart: Cart) =>
                miniCart?.cartId === updatedCart.cartId ? updatedCart : miniCart,
              );

              return {
                ...(current ?? {}),
                isError: false,
                data: nextMiniCarts,
              };
            },
            false,
          );
        }
        return;
      }

      mutate(getCartKey(targetCartId));
    },
    [getCartKey],
  );

  const orderCart = useCallback(async (cartId: string) => {
    const res = await sdk.callAction({ actionName: 'cart/checkout', query: { cartId } });
    void invalidateCartAndMiniCarts(getCartKey, cartId);

    return (res.isError ? {} : res.data) as Order;
  }, []);

  const createOrderForZeroPayment = useCallback(
    async (cartIdParam?: string) => {
      const extensions = sdk.composableCommerce;

      const targetCartId = cartIdParam || cartId || cartIdProp || '';

      const res = await extensions.cart.checkoutForM2h({
        cartId: targetCartId,
      });

      return (res.isError ? {} : res.data) as Order;
    },
    [cartId, cartIdProp],
  );

  const queryOrder = useCallback(
    async (orderId: string) => {
      const extensions = sdk.composableCommerce;

      const res = await extensions.cart.queryOrders({ orderIds: [orderId] });

      return (res.isError ? {} : res.data.items?.[0]) as Order;
    },
    [cartId],
  );

  const getOrder = useCallback(async (orderId: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getOrder({ orderId });
    mutate('/action/cart/getOrder');

    return (res.isError ? {} : res.data) as Order;
  }, []);

  const orderHistory = useCallback(async (limit = 5) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.queryOrders({ sortAttributes: [{ createdAt: 'desc' }], limit });

    return res.isError ? ([] as Order[]) : (res.data.items as Order[]);
  }, []);

  const orderHistoryFilters = useCallback(async (filters: OrderHistoryFilters = {}) => {
    const extensions = sdk.composableCommerce;

    const customFields: any = {};
    if (filters.fulfillmentType) customFields.fulfillmentType = filters.fulfillmentType;

    const query: any = {
      sortAttributes: filters.sortAttributes || [{ createdAt: 'desc' }],
      limit: filters.limit,
      cursor: filters.cursor,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      date: filters.date,
      orderStates: filters.orderState,
      ...(Object.keys(customFields).length > 0 && { customFields }),
    };

    const res = await extensions.cart.queryOrders(query);

    if (res.isError) {
      return {
        items: [] as Order[],
        total: 0,
        previousCursor: undefined,
        nextCursor: undefined,
        isError: true,
      };
    }

    return {
      items: res.data.items as Order[],
      total: res.data.total,
      previousCursor: res.data.previousCursor,
      nextCursor: res.data.nextCursor,
      isError: false,
    };
  }, []);

  const removeItem = useCallback(
    async (lineItemId: string) => {
      const extensions = sdk.composableCommerce;

      const payload = {
        lineItem: { id: lineItemId },
      };

      const res = await extensions.cart.removeItem(payload);
      void invalidateCartAndMiniCarts(getCartKey, cartId, res);
    },
    [cartId],
  );

  const removeItems = useCallback(
    async (payload: UpdateCartLineItemPayload) => {
      const extensions = sdk.composableCommerce;
      payload.cartId = cartId ?? cartIdProp;
      const res: any = await extensions.cart.updateLineItems(payload);
      if (!res.isError) {
        void invalidateCartAndMiniCarts(getCartKey, cartId, res);
        return false;
      } else {
        return true;
      }
    },
    [cartId, cartIdProp],
  );

  const updateItem = useCallback(
    async (lineItemId: string, newQuantity: number, shouldCalculateTaxes?: boolean) => {
      const extensions = sdk.composableCommerce;

      const payload = {
        cartId: cartId ?? cartIdProp,
        lineItem: {
          id: lineItemId,
          count: newQuantity,
        },
        shouldCalculateTaxes,
      };
      const res = await extensions.cart.updateItem(payload);
      void invalidateCartAndMiniCarts(getCartKey, cartId ?? cartIdProp, res);
    },
    [cartId],
  );

  const updateLineItems = useCallback(
    async (payload: UpdateCartLineItemPayload) => {
      const extensions = sdk.composableCommerce;

      const targetCartId = payload.cartId || cartId || cartIdProp || '';

      const finalPayload = {
        ...payload,
        cartId: targetCartId,
      };

      const res = await extensions.cart.updateLineItems(finalPayload);

      if (res.isError) {
        throw new Error(res.error?.message || 'Failed to update cart line items');
      }

      void invalidateCartAndMiniCarts(getCartKey, targetCartId, res);

      return res;
    },
    [cartId, cartIdProp, getCartKey],
  );

  const removeAllLineItems = useCallback(
    async (payload: { cartIds?: string[]; wishlistIds?: string[] }) => {
      const extensions = sdk.composableCommerce;

      const res = await extensions.cart.removeAllLineItems(payload);

      if (res.isError) {
        throw new Error(res.error?.message || 'Failed to delete wishlist/cart items');
      }

      mutate(miniCartsKey);

      payload.cartIds?.forEach((id) => {
        mutate(getCartKey(id));
      });

      return res.data;
    },
    [getCartKey],
  );

  const updateCart = useCallback(
    async (payload: CartDetails, taxMode?: string): Promise<Cart> => {
      const extensions = sdk.composableCommerce;

      const res = await extensions.cart.updateCart(payload);

      if (res.isError) {
        void invalidateCartAndMiniCarts(getCartKey, cartId, res);
        return {} as Cart;
      }

      const updatedCart = res.data as Cart;
      const lineItemCount = Array.isArray(updatedCart?.lineItems) ? updatedCart.lineItems.length : 0;

      if (lineItemCount < LARGE_CART_LINE_ITEM_LIMIT || taxMode === PLATFORM_TAX_MODE) {
        void invalidateCartAndMiniCarts(getCartKey, cartId, res);
        return updatedCart;
      }

      // Backend skips Vertex tax calc inline for large carts (updateCartFromRequest);
      // run it here as separate requests so each gets its own timeout budget.
      const taxesResponse = await sdk.callAction({
        actionName: 'cart/setCartTaxes',
        query: {
          cartId: updatedCart.cartId ?? cartId,
        },
      });

      if (taxesResponse.isError) {
        void invalidateCartAndMiniCarts(getCartKey, cartId, res);
        return {} as Cart;
      }

      const jurisdictionResponse = await sdk.callAction({
        actionName: 'cart/setJurisdictionIds',
        query: {
          cartId: updatedCart.cartId ?? cartId,
        },
      });

      if (jurisdictionResponse.isError) {
        void invalidateCartAndMiniCarts(getCartKey, cartId, taxesResponse);
        return {} as Cart;
      }

      void invalidateCartAndMiniCarts(getCartKey, cartId, jurisdictionResponse);

      return jurisdictionResponse.data as Cart;
    },
    [cartId, getCartKey],
  );

  const setShipping = useCallback(
    async (payload: SetCartShippingPayload, taxMode?: string): Promise<Cart> => {
      const extensions = sdk.composableCommerce;
      const shippingResponse = await extensions.cart.setShipping(payload);

      if (shippingResponse.isError) {
        void invalidateCartAndMiniCarts(getCartKey, cartId, shippingResponse);

        throw new Error(shippingResponse.error?.message ?? 'Unable to set shipping');
      }

      const shippingCart = shippingResponse.data as Cart;

      const lineItemCount = Array.isArray(shippingCart?.lineItems) ? shippingCart.lineItems.length : 0;

      if (lineItemCount < LARGE_CART_LINE_ITEM_LIMIT || taxMode === PLATFORM_TAX_MODE) {
        void invalidateCartAndMiniCarts(getCartKey, cartId, shippingResponse);

        return shippingCart;
      }

      const taxesResponse = await sdk.callAction({
        actionName: 'cart/setCartTaxes',
        query: {
          cartId: shippingCart.cartId ?? cartId,
        },
      });

      if (taxesResponse.isError) {
        void invalidateCartAndMiniCarts(getCartKey, cartId, shippingResponse);

        throw new Error(taxesResponse.error?.message ?? 'Unable to calculate cart taxes');
      }

      const jurisdictionResponse = await sdk.callAction({
        actionName: 'cart/setJurisdictionIds',
        query: {
          cartId: shippingCart.cartId ?? cartId,
        },
      });

      if (jurisdictionResponse.isError) {
        void invalidateCartAndMiniCarts(getCartKey, cartId, taxesResponse);

        throw new Error(jurisdictionResponse.error?.message ?? 'Unable to set cart jurisdiction ids');
      }

      void invalidateCartAndMiniCarts(getCartKey, cartId, jurisdictionResponse);

      return jurisdictionResponse.data as Cart;
    },
    [cartId],
  );

  const setShippingMethod = useCallback(
    async (payload: any): Promise<Cart> => {
      const extensions = sdk.composableCommerce;

      const res = await extensions.cart.setShippingMethod(payload);
      void invalidateCartAndMiniCarts(getCartKey, cartId, res);

      return (res.isError ? {} : res.data) as Cart;
    },
    [cartId, getCartKey],
  );

  const redeemDiscountCode = useCallback(
    async (code: string, cartIdProp?: string) => {
      const extensions = sdk.composableCommerce;

      const payload = {
        code: code,
        query: { cartId: cartIdProp ?? cartId },
      };
      const res = await extensions.cart.redeemDiscountCode(payload);

      if (!res.isError && (res.data as Cart).cartId) {
        void invalidateCartAndMiniCarts(getCartKey, cartIdProp ?? cartId, res);
      } else {
        throw new Error((res.isError ? res.error.message : '') ?? 'code not valid');
      }
    },
    [cartId],
  );

  const removeDiscountCode = useCallback(
    async (discount: DiscountCode, cartIdProp?: string) => {
      const extensions = sdk.composableCommerce;

      const res = await extensions.cart.removeDiscountCode({
        discountCodeId: discount.discountCodeId as string,
        query: { cartId: cartIdProp ?? cartId },
      });

      void invalidateCartAndMiniCarts(getCartKey, cartIdProp ?? cartId, res);
    },
    [cartId],
  );

  const resetCart = useCallback(async () => {
    await sdk.callAction({ actionName: 'cart/resetCart' });
  }, []);

  const setPickInfos = useCallback(async (cartId: string, payload: any): Promise<Cart> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.setPickInfos({ cartId }, payload);

    void invalidateCartAndMiniCarts(getCartKey, cartId, res);

    return (res.isError ? {} : res.data) as Cart;
  }, []);

  const cancelOrder = useCallback(async (orderId: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.requestOrderCancellation({ orderId });
    mutate('/action/cart/cancelOrder');

    return (res.isError ? res : res.data) as Order;
  }, []);

  const getPersonalizedCartBanner = useCallback(async (query: PersonalizedCartPayload) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getPersonalizedCartBanner(query);

    return res.isError ? res : res.data;
  }, []);

  return {
    data: cartData,
    totalItems,
    isLoading,
    isEmpty,
    isShippingAccurate,
    hasOutOfStockItems,
    transaction,
    addItem,
    updateCart,
    setShipping,
    setShippingMethod,
    removeItem,
    removeItems,
    updateItem,
    shippingMethods,
    shippingMethodsData,
    orderCart,
    createOrderForZeroPayment,
    queryOrder,
    getOrder,
    orderHistory,
    redeemDiscountCode,
    removeDiscountCode,
    resetCart,
    getCartById,
    miniCarts,
    miniCartsLoaded: !miniCartsLoading,
    updateCachedCart,
    updateLineItems,
    setPickInfos,
    orderHistoryFilters,
    cancelOrder,
    removeAllLineItems,
    getPersonalizedCartBanner,
  };
};

export default useCart;
